"use client";

import {
  Suspense,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
} from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { isChatShareProvider } from "@/src/lib/chat-share/url-policy";
import type { PdfFontKey } from "@/src/lib/pdf/fonts";
import type { PdfOptions } from "@/src/lib/pdf/print-document";
import { isRepoMarkdownImportQuery } from "@/src/lib/repo-markdown/url-policy";
import type { EditorPreferencesSource } from "@/src/lib/storage/user-preferences";
import {
  clearEditorHandoffReplay,
  clearEditorHandoffStorage,
  EDITOR_BLANK_QUERY_PARAM,
  EDITOR_CHAT_SHARE_IMPORT_QUERY_PARAM,
  EDITOR_HANDOFF_QUERY_PARAM,
  markdownEditorUrl,
  takeEditorHandoff,
} from "../editor/editor-handoff";
import { EditorShell } from "../editor-shell";
import { SiteHeader } from "../home/site-header";

type EditorSeed =
  | { status: "loading" }
  | {
      status: "ready";
      markdown?: string;
      filename?: string;
      editorSource: EditorPreferencesSource;
      pdfOptions?: PdfOptions;
      editorFontFamily?: PdfFontKey;
      editorFontSize?: number;
    };

const emptySubscribe = () => () => undefined;

function EditorLoadingShell() {
  return (
    <main
      data-app-shell-mode="fullscreen"
      className="flex min-h-0 flex-col overflow-hidden bg-zinc-100 text-zinc-950 h-dvh md:min-h-[40rem]"
    >
      <SiteHeader />
      <h1 className="sr-only">Fullscreen Markdown to PDF Editor</h1>
    </main>
  );
}

function seedFromHandoffId(handoffId: string): EditorSeed {
  const handoff = takeEditorHandoff(handoffId);
  clearEditorHandoffStorage();
  if (handoff) {
    return {
      status: "ready",
      markdown: handoff.markdown,
      filename: handoff.filename,
      editorSource: handoff.source ?? "editor",
      pdfOptions: handoff.settings?.pdfOptions,
      editorFontFamily: handoff.settings?.editorFontFamily,
      editorFontSize: handoff.settings?.editorFontSize,
    };
  }
  return { status: "ready", editorSource: "editor" };
}

function MarkdownEditorClientInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const handoffId = searchParams.get(EDITOR_HANDOFF_QUERY_PARAM);
  const isBlank = searchParams.get(EDITOR_BLANK_QUERY_PARAM) === "1";
  const importParam = searchParams.get(EDITOR_CHAT_SHARE_IMPORT_QUERY_PARAM);
  const repoMarkdownImport = isRepoMarkdownImportQuery(importParam);
  const chatShareProvider =
    !repoMarkdownImport && isChatShareProvider(importParam)
      ? importParam
      : undefined;
  // false on the server and during hydration so SSR HTML stays stable; true
  // only after hydration, when sessionStorage handoff is safe to read.
  const isClient = useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );
  // Blank is URL-driven, so server and client can both start empty.
  const [seed, setSeed] = useState<EditorSeed>(() =>
    isBlank
      ? { status: "ready", markdown: "", filename: "", editorSource: "editor" }
      : { status: "loading" },
  );
  const [seenHandoffId, setSeenHandoffId] = useState<
    string | null | undefined
  >(undefined);

  if (isClient && !isBlank && seenHandoffId !== handoffId) {
    setSeenHandoffId(handoffId);
    if (handoffId) {
      setSeed(seedFromHandoffId(handoffId));
    } else {
      clearEditorHandoffReplay();
      if (seed.status === "loading") {
        setSeed({ status: "ready", editorSource: "editor" });
      }
    }
  }

  useLayoutEffect(() => {
    if (!isBlank && !handoffId) {
      return;
    }
    router.replace(
      markdownEditorUrl(
        repoMarkdownImport
          ? { repoMarkdownImport: true }
          : chatShareProvider
            ? { chatShareProvider }
            : undefined,
      ),
      {
        scroll: false,
      },
    );
  }, [chatShareProvider, handoffId, isBlank, repoMarkdownImport, router]);

  // Uploaded handoffs wait until after hydration; blank can render immediately.
  if ((!isBlank && !isClient) || seed.status === "loading") {
    return <EditorLoadingShell />;
  }

  return (
    <EditorShell
      key={
        seed.markdown === undefined
          ? "default"
          : `handoff:${seed.filename}:${seed.markdown.length}`
      }
      mode="fullscreen"
      initialMarkdown={seed.markdown}
      initialFilename={seed.filename}
      editorSource={seed.editorSource}
      initialPdfOptions={seed.pdfOptions}
      initialEditorFontFamily={seed.editorFontFamily}
      initialEditorFontSize={seed.editorFontSize}
      chatShareProvider={chatShareProvider}
      repoMarkdownImport={repoMarkdownImport}
    />
  );
}

export function MarkdownEditorClient() {
  return (
    <Suspense fallback={<EditorLoadingShell />}>
      <MarkdownEditorClientInner />
    </Suspense>
  );
}
